import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, HeartHandshake, RefreshCw } from 'lucide-react';
import Button from './ui/Button';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { resolveFunctionsTarget } from '../utils/environmentConfig';
import { trackException, getCurrentTraceId } from '../services/appInsightsService';

/**
 * NonprofitApplicationsQueue — operator review of nonprofit / education Event Pass discount
 * applications (Backlog row 23). Backed by GET /platform/nonprofit-applications (platform level 3)
 * and POST /platform/nonprofit-applications/{tenantId}/decision (level 4).
 *
 * Rules, the same ones the Client switches panel follows:
 *   1. A failed LOAD renders an error and no rows — an empty queue shown because the GET failed
 *      would read as "nothing to review".
 *   2. A row's status changes only after the server answers; a stale or lost race reloads the queue.
 *   3. An UNRESOLVED backend target sends no authenticated request.
 *   4. Rejecting needs a reason: the applicant sees it on the website and in the decision email.
 *
 * Target turnaround is one business day, so the pending queue is the default view, oldest first.
 * English copy only — AdminPortal carries no i18n framework.
 */

const QUEUE_PATH = '/platform/nonprofit-applications';
const FILTERS = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'revoked', label: 'Revoked' },
    { value: 'all', label: 'All' },
];
const TYPE_LABELS = { nonprofit: 'Nonprofit', education: 'Education', religious: 'Religious organization' };

const traceIdHeader = () => getCurrentTraceId() || `adminportal-${Date.now().toString(36)}`;
const formatTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const metaStyle = { fontSize: '12px', color: 'var(--text-secondary, #6b7280)', margin: '0 0 12px 0' };
// Long websites and emails wrap inside the card on a phone instead of running past its edge.
const valueStyle = { margin: '0 0 6px 0', minWidth: 0, overflowWrap: 'anywhere' };
const termStyle = { fontWeight: 600, fontSize: 12, color: 'var(--text-secondary, #6b7280)' };
// Explicit token colours: a UA-default textarea gives axe no background to measure contrast against.
const reasonStyle = {
    marginTop: 4,
    padding: '8px 10px',
    font: 'inherit',
    borderRadius: 6,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--bg-card, #fff)',
    color: 'var(--text-primary, #111827)',
};
const noticeStyle = (isError) => ({
    marginTop: '12px',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: isError
        ? 'color-mix(in srgb, var(--error, #dc2626) 12%, var(--bg-card, #fff))'
        : 'color-mix(in srgb, var(--success, #059669) 12%, var(--bg-card, #fff))',
    color: 'var(--text-primary, #111827)',
});
const noticeIconStyle = (isError) => ({ color: isError ? 'var(--error, #dc2626)' : 'var(--success, #059669)', flexShrink: 0 });

/** Reads `{error}` from a failed response, falling back to `fallback` when the body is not JSON. */
const errorMessageOf = async (response, fallback) => {
    try {
        const body = await response.json();
        if (body && typeof body.error === 'string' && body.error) return body.error;
    } catch {
        // Non-JSON body: use the fallback.
    }
    return fallback;
};

/** Keeps only rows that carry the fields the table and the decision call need. */
const rowsFromPayload = (payload) => {
    if (!Array.isArray(payload?.applications)) {
        throw new Error('The application queue response was missing required data.');
    }
    return payload.applications.filter((row) => typeof row?.tenantId === 'string' && typeof row?.applicationId === 'string');
};

const NonprofitApplicationsQueue = ({ readOnly = false }) => {
    const [filter, setFilter] = useState('pending');
    const [rows, setRows] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [status, setStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [busyTenant, setBusyTenant] = useState(null);
    const [reasons, setReasons] = useState({});
    const [nextToken, setNextToken] = useState(null);

    const target = useMemo(() => resolveFunctionsTarget(
        process.env.REACT_APP_FUNCTIONS_URL,
        process.env.REACT_APP_WEBSOCKET_URL,
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
    ), []);

    const load = useCallback(async (continuationToken = null) => {
        if (!target.resolved) return;
        setIsLoading(true);
        setLoadError(null);
        try {
            const page = continuationToken ? `&continuationToken=${encodeURIComponent(continuationToken)}` : '';
            const response = await fetchWithAuth(`${target.baseUrl}${QUEUE_PATH}?status=${filter}${page}`, {
                method: 'GET',
                headers: { 'X-Trace-Id': traceIdHeader() },
            });
            if (!response.ok) {
                throw new Error(await errorMessageOf(response, `Failed to load applications (HTTP ${response.status}).`));
            }
            const payload = await response.json();
            const pageRows = rowsFromPayload(payload);
            setRows((previous) => (continuationToken && previous ? [...previous, ...pageRows] : pageRows));
            setNextToken(typeof payload.continuationToken === 'string' && payload.continuationToken ? payload.continuationToken : null);
        } catch (err) {
            trackException(err, { errorType: 'NonprofitQueue_LoadFailed' });
            setRows(null);
            setNextToken(null);
            setLoadError(err.message || 'Failed to load applications.');
        } finally {
            setIsLoading(false);
        }
    }, [filter, target.baseUrl, target.resolved]);

    useEffect(() => {
        load();
    }, [load]);

    const decide = async (row, decision) => {
        if (readOnly || !target.resolved || busyTenant) return;
        setStatus(null);
        const reason = (reasons[row.tenantId] || '').trim();
        if (decision !== 'approve' && !reason) {
            setStatus({ type: 'error', message: `Write the reason the applicant will see before you ${decision}.` });
            return;
        }
        const question = {
            approve: `Approve ${row.organizationName}? Web Event Pass checkouts for this account will be charged 50% for 12 months`
                + ' (a renewal continues from the current end date).',
            reject: `Reject ${row.organizationName}? The applicant sees your reason and may apply again.`,
            revoke: `Revoke ${row.organizationName}'s discount now? Its next web Event Pass is charged the regular price;`
                + ' passes already bought keep their price.',
        }[decision];
        if (!window.confirm(question)) return;

        setBusyTenant(row.tenantId);
        try {
            const response = await fetchWithAuth(
                `${target.baseUrl}${QUEUE_PATH}/${encodeURIComponent(row.tenantId)}/decision`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-Trace-Id': traceIdHeader() },
                    body: JSON.stringify({
                        applicationId: decision === 'revoke' ? row.grantApplicationId : row.applicationId,
                        decision,
                        reason: reason || undefined,
                    }),
                },
            );
            if (response.status === 409) {
                const message = await errorMessageOf(response, 'This application changed.');
                setStatus({ type: 'error', message: `${message} Reloaded the queue.` });
                await load();
                return;
            }
            if (!response.ok) {
                throw new Error(await errorMessageOf(response, `Failed to save the decision (HTTP ${response.status}).`));
            }
            const saved = await response.json();
            setStatus({
                type: 'success',
                message: saved.status === 'approved'
                    ? `${row.organizationName} approved until ${formatTime(saved.expiresAtUtc)}.`
                    : `${row.organizationName} ${saved.status}.`,
            });
            await load();
        } catch (err) {
            trackException(err, { errorType: 'NonprofitQueue_DecideFailed' });
            setStatus({ type: 'error', message: err.message || 'Failed to save the decision.' });
        } finally {
            setBusyTenant(null);
        }
    };

    return (
        <section className="provider-policy-card" data-testid="nonprofit-queue" aria-labelledby="nonprofit-queue-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <HeartHandshake size={20} aria-hidden="true" />
                <h3 id="nonprofit-queue-title" style={{ margin: 0, overflowWrap: 'anywhere' }}>Nonprofit &amp; education applications</h3>
            </div>
            <p style={metaStyle}>
                Approve an application to give the account 50% off Event Passes bought on the website for 12 months.
                Check the registration number and website first. Target: decide within one business day.
                Backend target: {target.baseUrl}
                {!target.resolved && ' — UNRESOLVED. No backend URL is configured, so the queue is disabled.'}
            </p>

            <div role="group" aria-label="Filter applications" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {FILTERS.map((option) => (
                    <Button
                        key={option.value}
                        size="sm"
                        variant={filter === option.value ? 'primary' : 'ghost'}
                        aria-pressed={filter === option.value}
                        onClick={() => setFilter(option.value)}
                        data-testid={`nonprofit-filter-${option.value}`}
                    >
                        {option.label}
                    </Button>
                ))}
                <Button size="sm" variant="ghost" icon={<RefreshCw size={14} />} onClick={() => load()} disabled={isLoading || !target.resolved}>
                    Refresh
                </Button>
            </div>

            {isLoading && !rows && <p>Loading applications…</p>}

            {loadError && (
                <div style={noticeStyle(true)} role="alert">
                    <AlertCircle size={18} style={noticeIconStyle(true)} />
                    <span style={{ flex: 1 }}>{loadError}</span>
                </div>
            )}

            {status && (
                <div style={noticeStyle(status.type === 'error')} role={status.type === 'error' ? 'alert' : 'status'} data-testid="nonprofit-status">
                    {status.type === 'error'
                        ? <AlertCircle size={18} style={noticeIconStyle(true)} />
                        : <CheckCircle size={18} style={noticeIconStyle(false)} />}
                    <span>{status.message}</span>
                </div>
            )}

            {rows && rows.length === 0 && <p data-testid="nonprofit-empty">No applications in this view.</p>}

            {rows && rows.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
                    {rows.map((row) => (
                        <li key={row.tenantId} className="provider-policy-card" data-testid={`nonprofit-row-${row.tenantId}`}>
                            <h4 style={{ margin: '0 0 4px 0', overflowWrap: 'anywhere' }}>{row.organizationName}</h4>
                            <p style={metaStyle}>
                                {TYPE_LABELS[row.organizationType] || row.organizationType} · Status: <strong>{row.status}</strong>
                                {' '}· Submitted {formatTime(row.submittedAtUtc)}
                            </p>
                            <dl style={{ margin: '0 0 8px 0', fontSize: 13 }}>
                                <dt style={termStyle}>Registration no.</dt><dd style={valueStyle}>{row.registrationNumber}</dd>
                                <dt style={termStyle}>Website</dt>
                                <dd style={valueStyle}>
                                    <a href={row.website} target="_blank" rel="noopener noreferrer">{row.website}</a>
                                </dd>
                                <dt style={termStyle}>Contact</dt><dd style={valueStyle}>{row.contactName} ({row.contactEmail})</dd>
                                <dt style={termStyle}>Tenant</dt><dd style={valueStyle}><code style={{ overflowWrap: 'anywhere' }}>{row.tenantId}</code></dd>
                                {row.decidedAtUtc && (<><dt style={termStyle}>Decided</dt><dd style={valueStyle}>{formatTime(row.decidedAtUtc)} by {row.decidedByUserId}</dd></>)}
                                {row.decisionReason && (<><dt style={termStyle}>Reason</dt><dd style={valueStyle}>{row.decisionReason}</dd></>)}
                                {row.expiresAtUtc && (<><dt style={termStyle}>Expires</dt><dd style={valueStyle}>{formatTime(row.expiresAtUtc)}</dd></>)}
                            </dl>
                            {(row.status === 'pending' || row.discountActive === true) && (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', fontSize: 13, flex: '1 1 240px', minWidth: 0 }}>
                                        <label htmlFor={`nonprofit-reason-${row.tenantId}`} style={{ color: 'var(--text-primary, #111827)' }}>
                                            Reason (required to reject or revoke; shown to the applicant)
                                        </label>
                                        <textarea
                                            id={`nonprofit-reason-${row.tenantId}`}
                                            rows={2}
                                            maxLength={1000}
                                            style={reasonStyle}
                                            value={reasons[row.tenantId] || ''}
                                            onChange={(e) => setReasons({ ...reasons, [row.tenantId]: e.target.value })}
                                            disabled={readOnly || busyTenant === row.tenantId}
                                            data-testid={`nonprofit-reason-${row.tenantId}`}
                                        />
                                    </div>
                                    {row.status === 'pending' && (<>
                                    <Button
                                        size="sm"
                                        onClick={() => decide(row, 'approve')}
                                        disabled={readOnly || !target.resolved || Boolean(busyTenant)}
                                        data-testid={`nonprofit-approve-${row.tenantId}`}
                                    >
                                        Approve
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => decide(row, 'reject')}
                                        disabled={readOnly || !target.resolved || Boolean(busyTenant)}
                                        data-testid={`nonprofit-reject-${row.tenantId}`}
                                    >
                                        Reject
                                    </Button>
                                    </>)}
                                    {row.discountActive === true && typeof row.grantApplicationId === 'string' && (
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            onClick={() => decide(row, 'revoke')}
                                            disabled={readOnly || !target.resolved || Boolean(busyTenant)}
                                            data-testid={`nonprofit-revoke-${row.tenantId}`}
                                        >
                                            Revoke
                                        </Button>
                                    )}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {rows && nextToken && (
                <Button size="sm" variant="ghost" onClick={() => load(nextToken)} disabled={isLoading} data-testid="nonprofit-load-more">
                    Load more
                </Button>
            )}
        </section>
    );
};

export default NonprofitApplicationsQueue;
